import { Camera, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { MemberPhoto } from '@/components/ui/member-photo';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

import { ImageCropper } from './image-cropper';

/**
 * Upload and crop a member's directory photo as a passport-style portrait (3:4).
 *
 * Distinct from `AvatarUploader`, which is circular and meant for signed-in user
 * profile icons — not for membership records.
 */

export interface MemberPhotoUploaderProps {
  src?: string | null;
  name: string;
  onUpload: (blob: Blob) => Promise<void> | void;
  onRemove?: () => void;
  disabled?: boolean;
  size?: 'lg' | 'xl';
  labels: {
    change: string;
    remove: string;
    cropTitle: string;
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
    closeCropper?: string;
  };
  className?: string;
}

export const MemberPhotoUploader = ({
  src,
  name,
  onUpload,
  onRemove,
  disabled = false,
  size = 'xl',
  labels,
  className,
}: MemberPhotoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(
    () => () => {
      if (candidate) URL.revokeObjectURL(candidate);
    },
    [candidate],
  );

  return (
    <div className={cn('flex items-start gap-4', className)}>
      <MemberPhoto src={src} name={name} size={size} />

      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          leadingIcon={<Camera className="h-4 w-4" />}
        >
          {labels.change}
        </Button>

        {src && onRemove ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onRemove}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            className="text-danger-fg"
          >
            {labels.remove}
          </Button>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) setCandidate(URL.createObjectURL(file));
            event.target.value = '';
          }}
        />
      </div>

      <Dialog open={candidate !== null} onOpenChange={(open) => !open && setCandidate(null)}>
        <DialogContent size="md" closeLabel={labels.closeCropper}>
          <DialogHeader>
            <DialogTitle>{labels.cropTitle}</DialogTitle>
          </DialogHeader>

          {candidate ? (
            <ImageCropper
              src={candidate}
              aspect={3 / 4}
              circular={false}
              maxSize={720}
              applying={uploading}
              onCancel={() => setCandidate(null)}
              onCropped={async (blob) => {
                setUploading(true);
                try {
                  await onUpload(blob);
                  setCandidate(null);
                } finally {
                  setUploading(false);
                }
              }}
              labels={{
                zoom: labels.zoom,
                apply: labels.apply,
                cancel: labels.cancel,
                instructions: labels.instructions,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
